/**
 * Row selection for bulk actions: click toggles, Shift-click selects the range from the
 * last clicked row, Esc clears. Keys that leave the list drop out of the selection.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface Selection {
  keys: ReadonlySet<string>;
  /** The last row toggled without Shift: the start of the next Shift range. */
  anchor: string | null;
}

export const EMPTY_SELECTION: Selection = { keys: new Set(), anchor: null };

export function toggleKey(sel: Selection, key: string): Selection {
  const keys = new Set(sel.keys);
  if (keys.has(key)) keys.delete(key);
  else keys.add(key);
  return { keys, anchor: key };
}

/**
 * Shift-click: every row between the anchor and `key` (in display order) takes the state
 * the clicked row is about to get. Without an anchor in view it toggles one row.
 */
export function rangeSelect(order: readonly string[], sel: Selection, key: string): Selection {
  const a = sel.anchor == null ? -1 : order.indexOf(sel.anchor);
  const b = order.indexOf(key);
  if (a < 0 || b < 0) return toggleKey(sel, key);
  const on = !sel.keys.has(key);
  const keys = new Set(sel.keys);
  for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
    const k = order[i]!;
    if (on) keys.add(k);
    else keys.delete(k);
  }
  return { keys, anchor: sel.anchor };
}

export function selectAll(order: readonly string[]): Selection {
  return { keys: new Set(order), anchor: null };
}

/** True when every row in `order` is selected (and there is at least one). */
export function allSelected(order: readonly string[], keys: ReadonlySet<string>): boolean {
  return order.length > 0 && order.every((k) => keys.has(k));
}

/** Select every row in `order`, or clear when all of them already are. */
export function toggleAllIn(order: readonly string[], sel: Selection): Selection {
  return allSelected(order, sel.keys) ? EMPTY_SELECTION : selectAll(order);
}

/** Drop keys no longer present. Returns the same object when nothing changed. */
export function pruneSelection(sel: Selection, present: ReadonlySet<string>): Selection {
  if (!sel.keys.size) return sel;
  let changed = false;
  const keys = new Set<string>();
  for (const k of sel.keys) {
    if (present.has(k)) keys.add(k);
    else changed = true;
  }
  if (!changed) return sel;
  return { keys, anchor: sel.anchor != null && present.has(sel.anchor) ? sel.anchor : null };
}

export interface UseSelection {
  selected: ReadonlySet<string>;
  count: number;
  isSelected: (key: string) => boolean;
  /** Toggle one row; with `range` (Shift held) select from the anchor. */
  toggle: (key: string, range?: boolean) => void;
  /** Select every displayed row, or clear when all already are. */
  toggleAll: () => void;
  clear: () => void;
  /** Some but not all rows selected. */
  partial: boolean;
  all: boolean;
}

export interface UseSelectionOptions {
  escClears?: boolean;
  /**
   * The rows on screen, in the order they are drawn (grouped, sorted, held). Shift ranges and
   * Select all use it, so they never reach rows the person cannot see: rows in another group,
   * or new rows waiting behind a "3 new, show" bar. Read when the person acts, so a list may
   * pass a getter over a value it computes later in the same render. Defaults to `order`.
   */
  displayOrder?: () => readonly string[];
}

/**
 * Selection over a list. `order` is every row that exists (keys that leave it drop out of the
 * selection); `displayOrder` is what is on screen. Esc clears it while anything is selected.
 */
export function useSelection(order: readonly string[], opts: UseSelectionOptions = {}): UseSelection {
  const [sel, setSel] = useState<Selection>(EMPTY_SELECTION);
  const present = useMemo(() => new Set(order), [order]);
  const orderRef = useRef<() => readonly string[]>(() => order);
  orderRef.current = opts.displayOrder ?? (() => order);

  useEffect(() => setSel((s) => pruneSelection(s, present)), [present]);

  const clear = useCallback(() => setSel(EMPTY_SELECTION), []);
  const toggle = useCallback((key: string, range?: boolean) => {
    const shown = orderRef.current();
    setSel((s) => (range ? rangeSelect(shown, s, key) : toggleKey(s, key)));
  }, []);
  const toggleAll = useCallback(() => {
    const shown = orderRef.current();
    setSel((s) => toggleAllIn(shown, s));
  }, []);

  const escClears = opts.escClears ?? true;
  const count = sel.keys.size;
  useEffect(() => {
    if (!escClears || !count) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;
      setSel(EMPTY_SELECTION);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [escClears, count]);

  const keys = sel.keys;
  const isSelected = useCallback((k: string) => keys.has(k), [keys]);
  return {
    selected: keys,
    count,
    isSelected,
    toggle,
    toggleAll,
    clear,
    partial: count > 0 && count < order.length,
    all: count > 0 && count >= order.length,
  };
}
